<template>
  <!-- Native radio inputs: one tab stop, arrow keys move and select, screen readers announce "n of 5". -->
  <fieldset class="star-rating" :aria-describedby="describedby || undefined">
    <legend class="sr-only">{{ label || $t('report.rating.group') }}</legend>
    <div class="stars" @mouseleave="hover = 0">
      <template v-for="i in 5" :key="i">
        <input
          :id="`${gid}-${i}`"
          class="sr-only"
          type="radio"
          :name="gid"
          :value="i"
          :checked="i === rating"
          @change="setRating(i)"
        />
        <label :for="`${gid}-${i}`" :title="$t('report.rating.starLabel', { n: i }, i)" @mouseenter="hover = i">
          <span class="sr-only">{{ $t('report.rating.starLabel', { n: i }, i) }}</span>
          <i :class="[i <= (hover || rating) ? 'fas' : 'far', 'fa-star']" aria-hidden="true"></i>
        </label>
      </template>
    </div>
    <p v-if="showStatus" class="rating-status" role="status">
      {{ rating ? $t('report.rating.yours', { n: rating }) : $t('report.rating.none') }}
    </p>
  </fieldset>
</template>

<script>
export default {
  name: 'StarRating',
  props: {
    initialRating: {
      type: Number,
      default: 0
    },
    uniqueID: {
      type: String,
      default: null
    },
    // accessible name of the group, e.g. the category being rated
    label: {
      type: String,
      default: ''
    },
    // id of an element that describes what is being rated
    describedby: {
      type: String,
      default: ''
    },
    // show "Your rating: 3 of 5" under the stars
    showStatus: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      ratings: this.getRatings(),
      hover: 0
    }
  },
  computed: {
    rating() {
      return this.ratings[this.uniqueID] || this.initialRating;
    },
    gid() {
      return 'star-' + String(this.uniqueID || this._uid || this.$.uid).replace(/[^a-zA-Z0-9_-]/g, '');
    }
  },
  methods: {
    setRating(rating) {
      const existingRatings = this.getRatings();
      const updatedRatings = {
        ...existingRatings,
        [this.uniqueID]: rating
      };
      this.ratings = updatedRatings;
      localStorage.setItem('ratings', JSON.stringify(updatedRatings));
      // print uniqueID and rating to console
      console.log(`uniqueID: ${this.uniqueID}, rating: ${rating}`);
      // if not uniqueID  include video or advice, then update the rating in the database

      if (this.uniqueID.includes('video') || this.uniqueID.includes('advice')) {
        // skip since AdviceSection got their own handler
      }else{
        this.updateRatings();
      }    
      this.$emit('input', rating);
    },
    getRatings() {
      const storedRatings = localStorage.getItem('ratings');
      return storedRatings ? JSON.parse(storedRatings) : {};
    },
    // upload rating to database 
    // first get from the uniqueID the category and the id
    // then update the rating in the database

    async updateRatings() {
      const category = this.uniqueID.split('-')[0];
      const id = this.uniqueID.split('-')[1];
      const categoryKey = 'rating_' + category;
      const categoryAmountKey = 'rating_' + category + '_amount';

      try {
        const record = await this.$pb.collection('reports').getOne(id);
        if (record) {
          const categoryValue = record[categoryKey] || 0;
          const categoryAmountValue = record[categoryAmountKey] || 0;

          const updatedRecord = {
            ...record,
            [categoryKey]: categoryValue + this.rating,
            [categoryAmountKey]: categoryAmountValue + 1,
          };

          await this.$pb.collection('reports').update(id, updatedRecord);
        }
      } catch (error) {
        console.error(`Error updating rating in reports record for id ${id}:`, error);
      }
    },


  }
}
</script>

<style scoped>
.star-rating {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  align-self: flex-start;
  border: 0;
  margin: 0;
  padding: 0;
  min-width: 0;
  --star-on: var(--color-icon-rating); /* flips in dark mode */
  --star-off: var(--color-border-input); /* 4.5:1 on white */
}
.stars {
  display: inline-flex;
}
/* 44x44 touch target, outline vs filled star so state is not carried by colour alone */
.stars label {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  min-height: 44px;
  font-size: 24px;
  cursor: pointer;
  color: var(--star-off);
}
.stars label .fas {
  color: var(--star-on);
}
.stars input:focus-visible + label {
  outline: 3px solid var(--color-focus);
  outline-offset: -2px;
  border-radius: 8px;
}
.rating-status {
  margin: 0;
  font-size: 14px;
  color: var(--color-secondary);
}
</style>
